import React, { useContext, useState } from 'react';

import PropTypes from 'prop-types';
import { Button, Form, Message, Modal } from 'semantic-ui-react';

import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { useDispatch } from 'react-redux';

import { flagBusinessSaved } from 'actions/business';
import CloseableModal from 'components/common/CloseableModal';
import HelpTooltip from 'components/common/HelpTooltip';
import {
  BusinessFileMetadataPropType,
  BusinessFilePropType,
} from 'components/modules/files/types/files';
import API from 'libs/api';
import ErrorHandler, { getErrorMessage } from 'libs/errors';

import {
  CATEGORY_HELP_TEXT,
  CATEGORY_PLACEHOLDER,
  DESCRIPTION_HELP_TEXT,
} from './MediaFileEditModal.constants';
import { getCategoryOptions, getInitialFormData } from './MediaFileEditModal.utils';
import { MediaContext } from '../../contexts/Media.context';

import './MediaFileEditModal.scss';

const propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func,
  file: BusinessFilePropType.isRequired,
  fileMetadata: BusinessFileMetadataPropType.isRequired,
};

const defaultProps = {
  onSave: null,
};

export default function MediaFileEditModal({
  open,
  onClose: onCloseProp,
  onSave: onSaveProp,
  file,
  fileMetadata,
}) {
  const dispatch = useDispatch();

  const { gmbLocation } = useContext(MediaContext);

  const [formData, setFormData] = useState(getInitialFormData(file, fileMetadata));
  const [isSaving, setIsSaving] = useState(false);
  const [touched, setTouched] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const { description, association } = formData;

  const fileMetadataId = _get(fileMetadata, 'id', null);

  function onChange(e, { name, value }) {
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value,
    }));
  }

  function getValidationError() {
    if (_isEmpty(association)) {
      return 'Category is required';
    }
    return null;
  }

  function clearModal() {
    setFormData(getInitialFormData(file, fileMetadata));
    setErrorMessage(null);
    setTouched(false);
  }

  function onClose() {
    clearModal();
    if (onCloseProp) {
      onCloseProp();
    }
  }

  async function onSave() {
    setTouched(true);
    setErrorMessage(null);

    const validationErrors = getValidationError();
    if (!_isEmpty(validationErrors)) {
      setErrorMessage(validationErrors);
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        metadata: {
          description,
          locationAssociationCategory: association,
        },
      };
      await API.updateBusinessFileProviderMetadata(fileMetadataId, payload);
      if (onSaveProp) {
        await onSaveProp();
      }
      dispatch(flagBusinessSaved(true));
      onClose();
    } catch (e) {
      ErrorHandler.capture(e);
      setErrorMessage(getErrorMessage(e));
    } finally {
      setIsSaving(false);
    }
  }

  const loading = isSaving;
  const disabled = isSaving || (touched && !_isEmpty(getValidationError()));

  return (
    <CloseableModal
      open={open}
      onClose={onClose}
      className="media-file-edit-modal"
      header="Edit Media"
      size="small"
    >
      <Modal.Content scrolling>
        <Form>
          <Form.Select
            label={
              <div className="label">
                Category <HelpTooltip title="Category" content={CATEGORY_HELP_TEXT} />
              </div>
            }
            name="association"
            options={getCategoryOptions(association, gmbLocation)}
            value={association}
            onChange={onChange}
            placeholder={CATEGORY_PLACEHOLDER}
            className={association.toLowerCase()}
          />

          <Form.Input
            label={
              <div className="label">
                Description <HelpTooltip title="Description" content={DESCRIPTION_HELP_TEXT} />
              </div>
            }
            name="description"
            onChange={onChange}
            className="description"
            value={description}
          />

          {!_isEmpty(errorMessage) && (
            <Message
              className="error-message"
              negative
              onDismiss={() => {
                setErrorMessage(null);
              }}
              header={errorMessage}
            />
          )}
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button
          content="Save"
          className="action"
          onClick={onSave}
          loading={loading}
          disabled={disabled}
        />
      </Modal.Actions>
    </CloseableModal>
  );
}

MediaFileEditModal.propTypes = propTypes;
MediaFileEditModal.defaultProps = defaultProps;
